import { FilterContentItem, FilterModalType } from '@/corePagesModule/filterModal/filterModal';
import { ImportFileType, IUnmanagedBusiness, SortKeyByFileType, SortKeyByFileTypeKeyNameArray } from '../entities/unmanagedBusiness';
import { DateService } from './dateService';
import { getExcelTypeFromFileName } from '@/appLogic/fileManager/translate/excelUtils';
import { convertStdInventoryIndexToDays } from '../inventoryIndices';
import {
  mergePlaceholderFields,
  normalizeUbNullDisplayValue,
  normalizeUbNullPackageDisplayValue,
} from '../constants/unmanagedBusinessAutoRegister';

/**
 * 未管理の取引のロジックを担当するサービス
 */
export class UnmanagedBusinessService {
  /**
   * 未管理取引に設定されたプレースホルダーフィールド一覧を返す
   * @param business - 未管理取引
   */
  private static getPlaceholderFields(business: IUnmanagedBusiness) {
    return mergePlaceholderFields(business.aggregatePlaceholderFields, business.basePlaceholderFields);
  }

  /**
   * エクセルファイル名からImportFileTypeに変換する
   * @param fileName - エクセルファイル名
   */
  public static covertToImportFileTypeFromExcelFileName(fileName: string): ImportFileType | null {
    // ファイル名からファイルタイプを抽出する
    const fileType = getExcelTypeFromFileName(fileName);
    // エクセルファイル名の中に、在庫、内示、販売実績の文字列が含まれているかを判定する
    if (fileType === '在庫') {
      return ImportFileType.Inventory;
    } else if (fileType === '内示') {
      return ImportFileType.Announce;
    } else if (fileType === '販売実績') {
      return ImportFileType.SalesResult;
    } else if (fileType === '入荷') {
      return ImportFileType.Arrival;
    } else {
      return null;
    }
  }

  /**
   * 月あたりの内示数量を計算する
   * @param business - 未管理取引
   * @param monthDiff - 当月の差分
   */
  public static calculateAnnounceAmountPerMonth(business: IUnmanagedBusiness, monthDiff: number): number {
    const dateObj = DateService.getShiftedIYearMonthFromNowDateByMonths(monthDiff);
    return (
      business.announce
        ?.filter((announce) => DateService.equalYearMonthInfo(announce, dateObj))
        .reduce((acc, cur) => acc + cur.amount, 0) ?? 0
    );
  }

  /**
   * 販売実績の合計を計算する
   * @param business - 未管理取引
   */
  public static calculateTotalSalesResult(business: IUnmanagedBusiness): number {
    return business.salesResults?.reduce((acc, cur) => acc + cur.amount, 0) ?? 0;
  }

  /**
   * FilterModalTypeからImportFileTypeに変換する
   * @param filterModalType - FilterModalType
   */
  public static convertFilterModalTypeToImportFileType(filterModalType: FilterModalType): ImportFileType {
    switch (filterModalType) {
      case 'unmanagedBusinessTypeAnnounce':
        return ImportFileType.Announce;
      case 'unmanagedBusinessTypeSalesResult':
        return ImportFileType.SalesResult;
      case 'unmanagedBusinessTypeInventory':
        return ImportFileType.Inventory;
      case 'unmanagedBusinessTypeArrival':
        return ImportFileType.Arrival;
      default:
        throw new Error('convertFilterModalTypeToImportFileType: 不正なFilterModalTypeが指定されました');
    }
  }

  /**
   * FilterContentItem[] で未管理取引配列をフィルタリングする
   * @param unmanagedBusinesses - 未管理取引配列
   * @param filterItems - フィルターアイテム
   */
  public static filterUnmanagedBusinesses(
    unmanagedBusinesses: IUnmanagedBusiness[],
    filterItems: FilterContentItem[]
  ): IUnmanagedBusiness[] {
    return unmanagedBusinesses.filter((business) => {
      for (const filterItem of filterItems) {
        switch (filterItem.filterType) {
          case 'UNMANAGED_BUSINESS_ANNOUNCE_AMOUNT-1':
            const announceAmountNowMonth = UnmanagedBusinessService.calculateAnnounceAmountPerMonth(business, 0);
            const announceAmount1SelectedValues = filterItem.selectedValue.map((value) => parseInt(value, 10));
            return announceAmount1SelectedValues.includes(announceAmountNowMonth);
          case 'UNMANAGED_BUSINESS_ANNOUNCE_AMOUNT-2':
            const announceAmountNextMonth = UnmanagedBusinessService.calculateAnnounceAmountPerMonth(business, 1);
            const announceAmountNextMonthSelectedValues = filterItem.selectedValue.map((value) => parseInt(value, 10));
            return announceAmountNextMonthSelectedValues.includes(announceAmountNextMonth);
          case 'UNMANAGED_BUSINESS_ANNOUNCE_AMOUNT-3':
            const announceAmountNext2Month = UnmanagedBusinessService.calculateAnnounceAmountPerMonth(business, 2);
            const announceAmountNext2MonthSelectedValues = filterItem.selectedValue.map((value) => parseInt(value, 10));
            return announceAmountNext2MonthSelectedValues.includes(announceAmountNext2Month);
          case 'UNMANAGED_BUSINESS_SALES_RESULTS_AMOUNT':
            const totalSalesResult = UnmanagedBusinessService.calculateTotalSalesResult(business);
            const totalSalesResultSelectedValues = filterItem.selectedValue.map((value) => parseInt(value, 10));
            return totalSalesResultSelectedValues.includes(totalSalesResult);
          case 'UNMANAGED_BUSINESS_ARRIVAL_AMOUNT':
            const arrivalAmountSelectedValues = filterItem.selectedValue.map((value) => parseInt(value, 10));
            return arrivalAmountSelectedValues.includes(business.arrival ?? 0);
          case 'UNMANAGED_BUSINESS_SUPPLIER_NUMBER':
            return filterItem.selectedValue.includes(business.supplierNumber);
          case 'UNMANAGED_BUSINESS_SUPPLIER_NAME':
            return filterItem.selectedValue.includes(business.supplierName);
          case 'UNMANAGED_BUSINESS_BUSINESS_NUMBER':
            return filterItem.selectedValue.includes(business.businessNumber);
          case 'UNMANAGED_BUSINESS_BUSINESS_NAME':
            return filterItem.selectedValue.includes(business.businessName ?? '');
          case 'UNMANAGED_BUSINESS_PACKAGE':
            return filterItem.selectedValue.includes(
              normalizeUbNullPackageDisplayValue(business.package, this.getPlaceholderFields(business)) ?? ''
            );
          case 'UNMANAGED_BUSINESS_WAREHOUSE_NUMBER':
            return filterItem.selectedValue.includes(business.warehouseNumber);
          case 'UNMANAGED_BUSINESS_WAREHOUSE':
            return filterItem.selectedValue.includes(business.warehouse);
          case 'UNMANAGED_BUSINESS_INVENTORY_AMOUNT':
            const inventoryAmountSelectedValues = filterItem.selectedValue.map((value) => parseInt(value, 10));
            return inventoryAmountSelectedValues.includes(business.inventory ?? 0);
          case 'UNMANAGED_BUSINESS_CUSTOMER_NUMBER':
            return filterItem.selectedValue.includes(
              normalizeUbNullDisplayValue(business.customerNumber, this.getPlaceholderFields(business), 'customerNumber') ?? ''
            );
          case 'UNMANAGED_BUSINESS_CUSTOMER_NAME':
            return filterItem.selectedValue.includes(
              normalizeUbNullDisplayValue(business.customerName, this.getPlaceholderFields(business), 'customerName') ?? ''
            );
          case 'UNMANAGED_BUSINESS_SALES_DESTINATION_NUMBER':
            return filterItem.selectedValue.includes(
              normalizeUbNullDisplayValue(business.salesDestinationNumber, this.getPlaceholderFields(business), 'salesDestinationNumber') ??
                ''
            );
          case 'UNMANAGED_BUSINESS_SALES_DESTINATION_NAME':
            return filterItem.selectedValue.includes(
              normalizeUbNullDisplayValue(business.salesDestinationName, this.getPlaceholderFields(business), 'salesDestinationName') ?? ''
            );
          case 'UNMANAGED_BUSINESS_END_USER':
            return filterItem.selectedValue.includes(
              normalizeUbNullDisplayValue(business.endUser, this.getPlaceholderFields(business), 'endUser') ?? ''
            );
          case 'UNMANAGED_BUSINESS_MANUFACTURER':
            return filterItem.selectedValue.includes(
              normalizeUbNullDisplayValue(business.manufacturer, this.getPlaceholderFields(business), 'manufacturer') ?? ''
            );
          default:
            throw new Error('filterUnmanagedBusinesses: 不正なFilterTypeが指定されました');
        }
      }
      return true;
    });
  }

  /**
   * 未管理取引配列をソートする
   * @param unmanagedBusinesses - 未管理取引配列
   * @param fileType - ファイルタイプ
   * @param isAsc - 昇順かどうか
   * @param sortTarget - ソート対象
   * @returns - ソートされた未管理取引配列
   */
  public static sortUnmanagedBusinesses(
    unmanagedBusinesses: IUnmanagedBusiness[],
    fileType: ImportFileType,
    isAsc: boolean,
    sortTarget?: string
  ): IUnmanagedBusiness[] {
    // ソート対象カラムなし、ソート対象未管理取引配列が1つ以下、ソート対象のカラム指定が誤っている場合はフィルタせず返す
    if (!sortTarget || unmanagedBusinesses.length < 2 || !SortKeyByFileTypeKeyNameArray[fileType].includes(sortTarget))
      return unmanagedBusinesses;

    let sortedUnmanagedBusiness: IUnmanagedBusiness[];
    switch (sortTarget) {
      case 'inputDate':
        sortedUnmanagedBusiness = unmanagedBusinesses.sort(
          (a, b) => DateService.convertIDateToUnixTime(a.inputDate) - DateService.convertIDateToUnixTime(b.inputDate)
        );
        break;
      case 'package':
        sortedUnmanagedBusiness = unmanagedBusinesses.sort(
          (a, b) =>
            (isNaN(parseFloat(a.package)) ? -1 : parseFloat(a.package)) - (isNaN(parseFloat(b.package)) ? -1 : parseFloat(b.package))
        );
        break;
      case 'customerAnnounce-1':
      case 'customerAnnounce-2':
      case 'customerAnnounce-3': {
        const monthDiff = parseInt(sortTarget.split('-')[1]) - 1;
        sortedUnmanagedBusiness = unmanagedBusinesses.sort(
          (a, b) => this.calculateAnnounceAmountPerMonth(a, monthDiff) - this.calculateAnnounceAmountPerMonth(b, monthDiff)
        );
        break;
      }
      case 'salesQuantity':
        sortedUnmanagedBusiness = unmanagedBusinesses.sort((a, b) => this.calculateTotalSalesResult(a) - this.calculateTotalSalesResult(b));
        break;
      case 'stdInventoryIndex':
        if (isAsc) {
          sortedUnmanagedBusiness = unmanagedBusinesses.sort(
            (a, b) =>
              convertStdInventoryIndexToDays(
                a.stdInventoryIndices?.標準在庫指数?.min ?? -1,
                a.stdInventoryIndices?.標準在庫指数?.option.unit ?? ''
              ) -
              convertStdInventoryIndexToDays(
                b.stdInventoryIndices?.標準在庫指数?.min ?? -1,
                b.stdInventoryIndices?.標準在庫指数?.option.unit ?? ''
              )
          );
        } else {
          sortedUnmanagedBusiness = unmanagedBusinesses.sort(
            (a, b) =>
              convertStdInventoryIndexToDays(
                b.stdInventoryIndices?.標準在庫指数?.max ?? -1,
                b.stdInventoryIndices?.標準在庫指数?.option.unit ?? ''
              ) -
              convertStdInventoryIndexToDays(
                a.stdInventoryIndices?.標準在庫指数?.max ?? -1,
                a.stdInventoryIndices?.標準在庫指数?.option.unit ?? ''
              )
          );
        }
        break;
      case 'inventory':
        sortedUnmanagedBusiness = unmanagedBusinesses.sort((a, b) => (a.inventory ?? -1) - (b.inventory ?? -1));
        break;
      case 'arrivalAmount':
        sortedUnmanagedBusiness = unmanagedBusinesses.sort((a, b) => (a.arrival ?? -1) - (b.arrival ?? -1));
        break;
      default:
        // ここに来るのは全て文字列比較
        sortedUnmanagedBusiness = unmanagedBusinesses.sort((a, b) => {
          const fieldName = SortKeyByFileType[fileType][sortTarget] as
            | 'businessNumber'
            | 'supplierNumber'
            | 'warehouseNumber'
            | 'customerNumber'
            | 'salesDestinationNumber'
            | 'customerName'
            | 'salesDestinationName'
            | 'manufacturer'
            | 'endUser';

          return (
            normalizeUbNullDisplayValue(a[fieldName] as string | undefined, this.getPlaceholderFields(a), fieldName) ?? ''
          ).localeCompare(normalizeUbNullDisplayValue(b[fieldName] as string | undefined, this.getPlaceholderFields(b), fieldName) ?? '');
        });
    }
    if (!isAsc && sortTarget !== 'stdInventoryIndex') {
      // DESCの場合は反転させるが、stdInventoryIndexはすでに前処理で判定しているので弾く
      sortedUnmanagedBusiness = sortedUnmanagedBusiness.reverse();
    }
    return sortedUnmanagedBusiness;
  }
}
